import { useEffect, useState } from "react";

/**
 * Eid Al Etihad 55 campaign (Eid is 2 Dec 2026) ends two weeks later:
 * 16 Dec 2026, 23:59:59 UAE time (UTC+4). Single source of truth for the home
 * hero and the site-wide header theme.
 */
export const EID55_END = new Date("2026-12-16T23:59:59+04:00");

/**
 * Master switch for the "Eid Al Etihad" header item. Set to false to hide it
 * immediately; otherwise it disappears on its own after EID55_END.
 */
export const EID55_NAV_ENABLED = true;

export const isEid55Active = (now: Date = new Date()): boolean =>
  now.getTime() <= EID55_END.getTime();

/**
 * react-snap bakes the campaign state at build time into the HTML. To avoid a
 * hydration mismatch when the campaign ends after that build, components'
 * first client render mirrors what the snapshot contains (the Header stripe
 * or the EidHero is present only while the campaign is on); they then re-sync
 * to the live date in an effect. Once any effect has run, hydration is over
 * and later mounts use the live value directly.
 */
const snapshotEid55: boolean | null =
  typeof document !== "undefined" &&
  document.getElementById("root")?.hasChildNodes()
    ? !!document.querySelector("[data-eid55-stripe], .eid-hero")
    : null;

let hydrating = snapshotEid55 !== null;

export const getInitialEid55 = (): boolean =>
  hydrating && snapshotEid55 !== null ? snapshotEid55 : isEid55Active();

export const useEid55Active = (): boolean => {
  const [active, setActive] = useState(getInitialEid55);
  useEffect(() => {
    hydrating = false;
    setActive(isEid55Active());
  }, []);
  return active;
};

/** Whether the header should show the Eid Al Etihad item (flag + date). */
export const useEid55Nav = (): boolean => {
  const active = useEid55Active();
  return EID55_NAV_ENABLED && active;
};
